import { Stack, TableCell, TableRow } from '@mui/material'
import React from 'react'
import { Link } from 'react-router'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatNumber,
} from '../../../../packages/utils/numberFormatters'
import { EnrichedIssueEntity, IssueEntity } from '../../../../types/IssueEntity'
import IssueTypeLabels from '../../../../components/entities/IssueTypeLabels'
import { hasSourceOfType } from '../../../../components/utils/calculateIssueStatus'
import { getIssueTypeLabel } from '../../../../utils/issues/getIssueTypeLabel'
import { NameChangeRowItem } from 'types/CompanyEntity'

interface IssueHistoryTableRowProps {
    date: string | null
    issue: EnrichedIssueEntity
    locale: string
    multipleInstrumentsExists: boolean
    splitAdjust: boolean
    isSmallScreen: boolean
    nameChangeRows: NameChangeRowItem[]
}

const getLinkedGuarantorIssues = (issue: IssueEntity) => {
    if (issue.linkedIssues.length === 0) {
        return []
    }

    const linkedGuarantorIssues = issue.linkedIssues.reduce((acc: Omit<IssueEntity, 'linkedIssues'>[], curr) => {
        if (curr.issues.length === 0) {
            return acc
        }

        const guarantorIssues = curr.issues.filter((issue) => issue.type === 'Guarantor fee issue')

        acc.push(...guarantorIssues)

        return acc
    }, [])

    return linkedGuarantorIssues
}

export const IssueHistoryTableRow = ({
    date,
    issue,
    locale,
    multipleInstrumentsExists = false,
    splitAdjust = false,
    isSmallScreen,
    nameChangeRows,
}: IssueHistoryTableRowProps) => {
    const unitSubPrice = issue.unitSubPrice ?? 0
    const sharesInUnit = issue.sharesInUnit ?? 1
    const unitSubTotal = issue.unitSubTotal ?? 0
    const subPricePerShare = sharesInUnit === 0 ? null : unitSubPrice / sharesInUnit
    const sumSharesSubscribed = sharesInUnit === 0 ? null : unitSubTotal * sharesInUnit
    const shouldShowOverAllotmentAllocation =
        issue.overAllotmentAllocation !== null && issue.overAllotmentAllocation > 0
    const shouldShowOverAllotmentAllocationSecondary =
        issue.overAllotmentAllocationSecondary !== null && issue.overAllotmentAllocationSecondary > 0
    const shouldShowSecondarySub = issue.secondarySub !== null && issue.secondarySub > 0

    const linkedGuarantorIssues = getLinkedGuarantorIssues(issue)

    const isCancelled = hasSourceOfType(issue, 'Cancellation')

    const numberOfShares = (() => {
        if (isCancelled) {
            return 'Avbruten'
        } else if (issue.totalOfferedUnits === 0) {
            return '0 nya aktier'
        } else if (sumSharesSubscribed === 0) {
            return 'Inte kommunicerat ännu'
        } else if (sumSharesSubscribed === null) {
            return '-'
        }
        return formatNumber(sumSharesSubscribed / (splitAdjust ? issue.splitAdjustment : 1), {
            locale,
            decimals: 0,
        })
    })()

    const oldestNameChange =
        nameChangeRows.length > 0
            ? nameChangeRows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0]
            : null

    return (
        <>
            <TableRow
                sx={{
                    fontStyle: isCancelled ? 'italic' : 'normal',
                    ...(shouldShowOverAllotmentAllocation ||
                    shouldShowSecondarySub ||
                    shouldShowOverAllotmentAllocationSecondary ||
                    linkedGuarantorIssues.length > 0
                        ? {
                              '& .MuiTableCell-root': {
                                  borderBottom: 0,
                              },
                          }
                        : {}),
                }}
            >
                <TableCell>{formatDateYYYYMMDD(date)}</TableCell>
                {multipleInstrumentsExists && !isSmallScreen ? <TableCell>{issue.instrumentSymbol}</TableCell> : null}
                <TableCell>
                    <Stack direction='row' spacing={2}>
                        <IssueTypeLabels issue={issue} />
                        <Link to={`/issues/${issue.id}`}>
                            {getIssueTypeLabel(issue.type, issue.earnout)}
                            {issue.type === 'Non-cash issue'
                                ? ` (${issue.noncashConsideration})`
                                : issue.type === 'IPO' && oldestNameChange?.item
                                  ? ` (${oldestNameChange.item.fromName})`
                                  : null}
                        </Link>
                    </Stack>
                </TableCell>
                {!isSmallScreen ? (
                    <>
                        <TableCell>
                            {subPricePerShare
                                ? formatMoney(subPricePerShare * (splitAdjust ? issue.splitAdjustment : 1), {
                                      locale,
                                  })
                                : '-'}
                        </TableCell>
                        <TableCell>{numberOfShares}</TableCell>
                    </>
                ) : null}
                <TableCell>
                    {sumSharesSubscribed && subPricePerShare
                        ? formatBigMoney(sumSharesSubscribed * subPricePerShare, { locale, decimals: 0, tooltip: true })
                        : '-'}
                </TableCell>
            </TableRow>
            {shouldShowOverAllotmentAllocation && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                            ...(shouldShowSecondarySub ||
                            shouldShowOverAllotmentAllocationSecondary ||
                            linkedGuarantorIssues.length > 0
                                ? {
                                      borderBottom: 0,
                                  }
                                : {}),
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>Övertilldelning</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(
                                    ((issue.overAllotmentAllocation as number) * sharesInUnit) /
                                        (splitAdjust ? issue.splitAdjustment : 1),
                                    { locale, decimals: 0 },
                                )}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {subPricePerShare
                            ? formatBigMoney(
                                  (issue.overAllotmentAllocation as number) * sharesInUnit * subPricePerShare,
                                  {
                                      locale,
                                      decimals: 0,
                                      tooltip: true,
                                  },
                              )
                            : '-'}
                    </TableCell>
                </TableRow>
            )}
            {shouldShowSecondarySub && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                            ...(shouldShowOverAllotmentAllocationSecondary || linkedGuarantorIssues.length > 0
                                ? {
                                      borderBottom: 0,
                                  }
                                : {}),
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>Befintliga aktier</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(
                                    ((issue.secondarySub as number) * sharesInUnit) /
                                        (splitAdjust ? issue.splitAdjustment : 1),
                                    { locale, decimals: 0 },
                                )}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {unitSubPrice ? (
                            <>
                                (
                                {formatBigMoney((issue.secondarySub as number) * unitSubPrice, {
                                    locale,
                                    decimals: 0,
                                    tooltip: true,
                                })}
                                )
                            </>
                        ) : (
                            '-'
                        )}
                    </TableCell>
                </TableRow>
            )}
            {shouldShowOverAllotmentAllocationSecondary && (
                <TableRow
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                            ...(linkedGuarantorIssues.length > 0
                                ? {
                                      borderBottom: 0,
                                  }
                                : {}),
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>Övertilldelning befintliga</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {formatNumber(
                                    ((issue.overAllotmentAllocationSecondary as number) * sharesInUnit) /
                                        (splitAdjust ? issue.splitAdjustment : 1),
                                    {
                                        locale,
                                        decimals: 0,
                                    },
                                )}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        (
                        {unitSubPrice
                            ? formatBigMoney((issue.overAllotmentAllocationSecondary as number) * unitSubPrice, {
                                  locale,
                                  decimals: 0,
                                  tooltip: true,
                              })
                            : '-'}
                        )
                    </TableCell>
                </TableRow>
            )}
            {linkedGuarantorIssues.map(({ id, unitSubTotal }) => (
                <TableRow
                    key={id}
                    sx={{
                        '& .MuiTableCell-root': {
                            paddingTop: 0,
                        },
                    }}
                >
                    <TableCell />
                    {multipleInstrumentsExists && !isSmallScreen ? <TableCell /> : null}
                    <TableCell>Garantiersättning</TableCell>
                    {!isSmallScreen ? (
                        <>
                            <TableCell />
                            <TableCell>
                                {unitSubTotal !== null && unitSubTotal > 0
                                    ? formatNumber(unitSubTotal / (splitAdjust ? issue.splitAdjustment : 1), {
                                          locale,
                                          decimals: 0,
                                      })
                                    : '-'}
                            </TableCell>
                        </>
                    ) : null}
                    <TableCell>
                        {unitSubTotal !== null && unitSubTotal > 0 && subPricePerShare
                            ? formatBigMoney(unitSubTotal * subPricePerShare, { locale, decimals: 0, tooltip: true })
                            : '-'}
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}
